import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import TwoFactorModal from '../components/TwoFactorModal';
import use2faProtect from '../hooks/use2faProtect';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import {
  FiUsers,
  FiActivity,
  FiShield,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiPackage,
  FiCreditCard,
  FiUserCheck,
  FiUserX,
  FiCalendar,
  FiAlertCircle,
  FiArrowLeft,
} from 'react-icons/fi';
import { formatAmount } from '../utils/currency';

/**
 * AdminDashboard - Enterprise Admin Portal
 * ARCHITECTURE RULES:
 * - Single Source of Truth for all metrics
 * - Admin is Controller, not Editor
 * - "Not available yet" for missing data, never zero
 * - All actions are auditable
 */
const AdminDashboard = () => {
  const { protect, show2fa, error2fa, handleVerify, handleClose } = use2faProtect();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [intelligenceMetrics, setIntelligenceMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchStatistics();
    fetchIntelligenceMetrics();
    fetchAssignableRoles();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'plans') {
      fetchPlans();
    }
  }, [activeTab, userPage, searchQuery]);

  const fetchAssignableRoles = async () => {
    try {
      const res = await adminAPI.getAssignableRoles();
      if (res.data?.data?.roles) setAssignableRoles(res.data.data.roles);
    } catch { /* non-critical - endpoint may not exist */ }
  };

  const fetchStatistics = async () => {
    try {
      const res = await adminAPI.getStatistics();
      setStats(res.data.data.statistics);
    } catch (error) {
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntelligenceMetrics = async () => {
    try {
      const res = await adminAPI.getIntelligenceMetrics();
      if (res.data.data.metrics) {
        setIntelligenceMetrics(res.data.data.metrics);
      }
    } catch (error) {
      // Silently fail - Phase 2 metrics may not be available
      console.log('Intelligence metrics not available:', error.message);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await adminAPI.listUsers({ page: userPage, limit: 20, search: searchQuery || undefined });
      setUsers(res.data.data.users);
      setUserTotal(res.data.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await adminAPI.getPlans();
      setPlans(res.data.data.plans);
    } catch (error) {
      toast.error('Failed to load plans');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    setActionLoading(userId);
    try {
      await protect(async (headers) => {
        await adminAPI.updateUserRole(userId, { role: newRole }, headers);
        toast.success(`Role updated to ${newRole}`);
        fetchUsers();
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePlan = async (userId, planName) => {
    setActionLoading(userId);
    try {
      await adminAPI.updateUserPlan(userId, { plan_name: planName });
      toast.success(`Plan updated to ${planName}`);
      fetchUsers();
      fetchStatistics(); // Refresh stats after plan change
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUserStatus = async (userId, action) => {
    setActionLoading(userId);
    try {
      await protect(async (headers) => {
        await adminAPI.updateUserStatus(userId, { action }, headers);
        toast.success(`User ${action}d successfully`);
        fetchUsers();
        fetchStatistics();
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(userTotal / 20);

  // Helper to render stat value or "Not available yet"
  const renderStatValue = (value, formatter = (v) => v) => {
    if (value === undefined || value === null) {
      return <span className="admin-stat-unavailable">Not available yet</span>;
    }
    return formatter(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">Platform overview and key metrics</p>
        </div>
        <Link to="/dashboard" className="admin-btn admin-btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiArrowLeft size={16} />
          Back to App
        </Link>
      </div>

      {/* Section Selector */}
      <div className="admin-section-nav">
        {[
          { id: 'overview', icon: <FiActivity size={14} />, label: 'Overview' },
          { id: 'users', icon: <FiUsers size={14} />, label: 'Users' },
          { id: 'subscriptions', icon: <FiCreditCard size={14} />, label: 'Subscriptions' },
          { id: 'plans', icon: <FiShield size={14} />, label: 'Plans' },
        ].map(tab => (
          <button
            key={tab.id}
            className={`admin-section-btn${activeTab === tab.id ? ' admin-section-btn--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* User KPIs */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiUsers size={20} />
                User Metrics
              </h2>
            </div>
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <span className="admin-stat-label">Total Users</span>
                <span className="admin-stat-value">
                  {renderStatValue(stats?.totalUsers)}
                </span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-label">
                  <FiUserCheck size={14} style={{ marginRight: 4 }} />
                  Active (30 days)
                </span>
                <span className="admin-stat-value">
                  {renderStatValue(stats?.activeUsers30d)}
                </span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-label">Total Vendors</span>
                <span className="admin-stat-value">
                  {renderStatValue(stats?.totalVendors)}
                </span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-label">
                  <FiUserX size={14} style={{ marginRight: 4 }} />
                  Suspended Users
                </span>
                <span className="admin-stat-value admin-stat-unavailable">
                  Not available yet
                </span>
              </div>
            </div>
          </div>

          {/* Platform Activity */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiActivity size={20} />
                Platform Activity
              </h2>
            </div>
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <span className="admin-stat-label">Total Transactions</span>
                <span className="admin-stat-value">
                  {renderStatValue(stats?.totalTransactions)}
                </span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-label">Active Ledgers</span>
                <span className="admin-stat-value">
                  {renderStatValue(stats?.totalLedgers)}
                </span>
              </div>
            </div>
          </div>

          {/* Financial Overview - CEO Level */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiDollarSign size={20} />
                Financial Overview
              </h2>
              <span className="admin-card-badge">Aggregate - All Tenants</span>
            </div>
            {stats?.platformFinancials ? (
              <div className="admin-stats-grid">
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #1BC5BD' }}>
                  <span className="admin-stat-label">
                    <FiTrendingUp size={14} style={{ marginRight: 4 }} />
                    Total Income
                  </span>
                  <span className="admin-stat-value" style={{ color: '#1BC5BD' }}>
                    {formatAmount(stats.platformFinancials.totalIncome)}
                  </span>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #F64E60' }}>
                  <span className="admin-stat-label">
                    <FiTrendingDown size={14} style={{ marginRight: 4 }} />
                    Total Expenses
                  </span>
                  <span className="admin-stat-value" style={{ color: '#F64E60' }}>
                    {formatAmount(stats.platformFinancials.totalExpenses)}
                  </span>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #FFA800' }}>
                  <span className="admin-stat-label">
                    <FiAlertCircle size={14} style={{ marginRight: 4 }} />
                    Outstanding Payables
                  </span>
                  <span className="admin-stat-value" style={{ color: '#FFA800' }}>
                    {formatAmount(stats.platformFinancials.totalPayables)}
                  </span>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #3699FF' }}>
                  <span className="admin-stat-label">Outstanding Receivables</span>
                  <span className="admin-stat-value" style={{ color: '#3699FF' }}>
                    {formatAmount(stats.platformFinancials.totalReceivables)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="admin-unavailable">
                <FiAlertCircle className="admin-unavailable-icon" />
                <p>Financial metrics not available yet</p>
              </div>
            )}
          </div>

          {/* System Payables */}
          {stats?.systemPayables && (stats.systemPayables.totalPayables > 0 || stats.systemPayables.totalReceivables > 0) && (
            <div className="admin-card" style={{ backgroundColor: '#FFFBF2' }}>
              <div className="admin-card-header">
                <h2 className="admin-card-title">
                  <FiPackage size={20} />
                  System / Miscellaneous Dues
                </h2>
                <span className="admin-card-badge">Non-Vendor Payables</span>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#7E8299', marginBottom: 16 }}>
                Dues not tied to specific vendors across all tenants (milk, rent, utilities, etc.)
              </p>
              <div className="admin-stats-grid">
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #FFA800', backgroundColor: '#FFFFFF' }}>
                  <span className="admin-stat-label">System Payables</span>
                  <span className="admin-stat-value" style={{ color: '#946C00' }}>
                    {formatAmount(stats.systemPayables.totalPayables)}
                  </span>
                </div>
                <div className="admin-stat-card" style={{ borderLeft: '4px solid #3699FF', backgroundColor: '#FFFFFF' }}>
                  <span className="admin-stat-label">System Receivables</span>
                  <span className="admin-stat-value" style={{ color: '#0062CC' }}>
                    {formatAmount(stats.systemPayables.totalReceivables)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Subscription KPIs */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiCreditCard size={20} />
                Subscription Metrics
              </h2>
              <span className="admin-card-badge">Source: subscriptions table</span>
            </div>
            {stats?.planDistribution && stats.planDistribution.length > 0 ? (
              <div className="admin-plan-distribution">
                {stats.planDistribution.map((plan, idx) => (
                  <div key={idx} className="admin-plan-dist-item">
                    <div className="admin-plan-dist-info">
                      <span className="admin-plan-dist-name">{plan.display_name || plan.name}</span>
                      <span className="admin-plan-dist-type">
                        {plan.name === 'basic' ? 'Free' : 'Paid'}
                      </span>
                    </div>
                    <div className="admin-plan-dist-count">{plan.user_count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-unavailable">
                <FiAlertCircle className="admin-unavailable-icon" />
                <p>Subscription data not available yet</p>
              </div>
            )}

            {/* Phase 2 Metrics - Show real data when available */}
            <div style={{ marginTop: 24 }}>
              <h4 style={{ fontSize: '0.9rem', color: '#7E8299', marginBottom: 12 }}>
                Growth Metrics
                {intelligenceMetrics && (
                  <span className="admin-card-badge" style={{ marginLeft: 8 }}>
                    Source: subscription_metrics
                  </span>
                )}
              </h4>
              <div className="admin-stats-grid">
                <div className="admin-stat-card">
                  <span className="admin-stat-label">Free → Paid Conversions</span>
                  {intelligenceMetrics?.current ? (
                    <span className="admin-stat-value" style={{ color: '#1BC5BD' }}>
                      {intelligenceMetrics.current.upgrades}
                      <span style={{ fontSize: '0.75rem', color: '#7E8299', marginLeft: 4 }}>
                        ({intelligenceMetrics.current.conversionRate.toFixed(1)}%)
                      </span>
                    </span>
                  ) : (
                    <span className="admin-stat-unavailable">Not available yet</span>
                  )}
                </div>
                <div className="admin-stat-card">
                  <span className="admin-stat-label">Churn Rate (30d)</span>
                  {intelligenceMetrics?.current ? (
                    <span className="admin-stat-value" style={{ color: intelligenceMetrics.current.churnRate > 5 ? '#F64E60' : '#7E8299' }}>
                      {intelligenceMetrics.current.churnRate.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="admin-stat-unavailable">Not available yet</span>
                  )}
                </div>
                <div className="admin-stat-card">
                  <span className="admin-stat-label">MRR</span>
                  {intelligenceMetrics?.current ? (
                    <span className="admin-stat-value" style={{ color: '#6C5CE7' }}>
                      {formatAmount(intelligenceMetrics.current.mrr)}
                    </span>
                  ) : (
                    <span className="admin-stat-unavailable">Not available yet</span>
                  )}
                </div>
                <div className="admin-stat-card">
                  <span className="admin-stat-label">ARPU</span>
                  {intelligenceMetrics?.current ? (
                    <span className="admin-stat-value">
                      {formatAmount(intelligenceMetrics.current.arpu)}
                    </span>
                  ) : (
                    <span className="admin-stat-unavailable">Not available yet</span>
                  )}
                </div>
              </div>

              {/* Retention Metrics */}
              {intelligenceMetrics?.retention && (
                <div style={{ marginTop: 16 }}>
                  <h5 style={{ fontSize: '0.85rem', color: '#B5B5C3', marginBottom: 8 }}>Retention</h5>
                  <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                      <span className="admin-stat-label">30-Day</span>
                      <span className="admin-stat-value">{intelligenceMetrics.retention.day30}%</span>
                    </div>
                    <div className="admin-stat-card">
                      <span className="admin-stat-label">60-Day</span>
                      <span className="admin-stat-value">{intelligenceMetrics.retention.day60}%</span>
                    </div>
                    <div className="admin-stat-card">
                      <span className="admin-stat-label">90-Day</span>
                      <span className="admin-stat-value">{intelligenceMetrics.retention.day90}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Growth */}
          {stats?.userGrowth && stats.userGrowth.length > 0 && (
            <div className="admin-card">
              <div className="admin-card-header">
                <h2 className="admin-card-title">
                  <FiCalendar size={20} />
                  User Growth
                </h2>
                <span className="admin-card-badge">Last 6 Months</span>
              </div>
              <div className="admin-growth-chart">
                {stats.userGrowth.map((item, idx) => (
                  <div key={idx} className="admin-growth-bar">
                    <div className="admin-growth-label">
                      {new Date(item.month).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                    </div>
                    <div className="admin-growth-value">+{item.signups}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <FiUsers size={20} />
              User Management
            </h2>
            <span className="admin-card-badge">{userTotal} total</span>
          </div>

          <div className="admin-search-wrapper">
            <FiSearch size={18} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setUserPage(1); }}
            />
          </div>

          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#7E8299' }}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const userStatus = u.status || (u.subscription_status === 'active' ? 'active' : 'inactive');
                    const canChangeRole = assignableRoles.length > 0 && !u.is_root;
                    return (
                    <tr key={u.id}>
                      <td>
                        <div className="admin-user-cell">
                          <span className="admin-user-name">
                            {u.full_name || 'No name'}
                            {u.is_root && <span className="admin-role-tag" style={{ background: '#F64E60' }}>ROOT</span>}
                          </span>
                          <span className="admin-user-email">{u.email}</span>
                        </div>
                      </td>
                      <td>
                        {canChangeRole ? (
                          <select
                            className="admin-plan-select"
                            value={u.role || 'user'}
                            onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                            disabled={actionLoading === u.id}
                            style={{ minWidth: 100 }}
                          >
                            {/* Always show current role even if not assignable */}
                            {!assignableRoles.includes(u.role) && u.role && (
                              <option value={u.role} disabled>{u.role}</option>
                            )}
                            {assignableRoles.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="admin-role-tag" style={{
                            background: u.role === 'super_admin' ? '#F64E60' : u.role === 'app_admin' ? '#6C5CE7' : '#3699FF',
                          }}>
                            {u.role || 'user'}
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className="admin-plan-select"
                          value={u.plan_name || 'basic'}
                          onChange={(e) => handleUpdatePlan(u.id, e.target.value)}
                          disabled={actionLoading === u.id}
                        >
                          <option value="basic">Basic</option>
                          <option value="pro">Pro</option>
                          <option value="premium">Premium</option>
                        </select>
                      </td>
                      <td>
                        <span className={`admin-status ${userStatus === 'active' ? 'active' : 'inactive'}`}>
                          {userStatus === 'active' ? 'Active' : userStatus === 'suspended' ? 'Suspended' : userStatus === 'disabled' ? 'Disabled' : 'Inactive'}
                        </span>
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div className="admin-action-buttons">
                          {userStatus === 'active' ? (
                            <button
                              className="admin-btn admin-btn-danger"
                              onClick={() => handleUserStatus(u.id, 'suspend')}
                              disabled={actionLoading === u.id || u.is_root}
                              title={u.is_root ? 'Cannot suspend root admin' : 'Suspend user'}
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              className="admin-btn admin-btn-success"
                              onClick={() => handleUserStatus(u.id, 'activate')}
                              disabled={actionLoading === u.id}
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-btn admin-btn-secondary"
                disabled={userPage <= 1}
                onClick={() => setUserPage(userPage - 1)}
              >
                <FiChevronLeft size={16} />
                Previous
              </button>
              <span className="admin-pagination-info">
                Page {userPage} of {totalPages}
              </span>
              <button
                className="admin-btn admin-btn-secondary"
                disabled={userPage >= totalPages}
                onClick={() => setUserPage(userPage + 1)}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <FiCreditCard size={20} />
              Subscription Management
            </h2>
            <span className="admin-card-badge">Source of Truth: subscriptions table</span>
          </div>

          <div className="admin-unavailable" style={{ marginBottom: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <FiCreditCard size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
              <h3 style={{ color: '#3F4254', marginBottom: 8 }}>Subscription Dashboard</h3>
              <p>Detailed subscription analytics coming in Phase 2</p>
              <p style={{ fontSize: '0.85rem', color: '#B5B5C3', marginTop: 8 }}>
                Will include: Active subscriptions, renewals, cancellations, revenue tracking
              </p>
            </div>
          </div>

          {/* Current Plan Distribution (read-only view) */}
          {stats?.planDistribution && stats.planDistribution.length > 0 && (
            <>
              <h3 style={{ fontSize: '1rem', color: '#3F4254', marginBottom: 16 }}>Current Distribution</h3>
              <div className="admin-plan-distribution">
                {stats.planDistribution.map((plan, idx) => (
                  <div key={idx} className="admin-plan-dist-item">
                    <div className="admin-plan-dist-info">
                      <span className="admin-plan-dist-name">{plan.display_name || plan.name}</span>
                    </div>
                    <div className="admin-plan-dist-count">{plan.user_count} users</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <FiShield size={20} />
              Subscription Plans
            </h2>
            <span className="admin-card-badge">Read-only</span>
          </div>

          <div className="admin-plans-grid">
            {plans.length === 0 ? (
              <div className="admin-unavailable">
                <FiAlertCircle className="admin-unavailable-icon" />
                <p>No plans configured</p>
              </div>
            ) : (
              plans.map((plan) => (
                <div key={plan.id} className="admin-plan-card">
                  <div className="admin-plan-header">
                    <h3>{plan.display_name}</h3>
                    <span className="admin-plan-price">
                      {parseFloat(plan.price_monthly) === 0
                        ? 'Free'
                        : `${formatAmount(plan.price_monthly)}/mo`
                      }
                    </span>
                  </div>
                  {plan.features && plan.features[0]?.feature_key && (
                    <ul className="admin-plan-features">
                      {plan.features.map((f, idx) => (
                        <li key={idx} className={f.is_enabled ? 'enabled' : 'disabled'}>
                          <span className="admin-feature-icon">
                            {f.is_enabled ? '✓' : '×'}
                          </span>
                          {f.feature_label}
                          {f.limit_value && (
                            <span className="admin-feature-limit">
                              (limit: {f.limit_value})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Inline Styles for admin-specific components */}
      <style>{`
        .admin-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          color: #7E8299;
        }
        .admin-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #EEF0F8;
          border-top-color: #6C5CE7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .admin-card-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          background-color: #F3F6F9;
          color: #7E8299;
          border-radius: 4px;
          font-weight: 500;
        }
        .admin-stat-unavailable {
          font-size: 0.85rem;
          color: #B5B5C3;
          font-style: italic;
        }
        .admin-plan-distribution {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .admin-plan-dist-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background-color: #F3F6F9;
          border-radius: 6px;
        }
        .admin-plan-dist-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .admin-plan-dist-name {
          font-weight: 500;
          color: #3F4254;
        }
        .admin-plan-dist-type {
          font-size: 0.75rem;
          padding: 2px 8px;
          background-color: #E8E5FF;
          color: #6C5CE7;
          border-radius: 4px;
        }
        .admin-plan-dist-count {
          font-size: 1.25rem;
          font-weight: 600;
          color: #181C32;
        }
        .admin-growth-chart {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          padding: 8px 0;
        }
        .admin-growth-bar {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 60px;
        }
        .admin-growth-label {
          font-size: 0.75rem;
          color: #7E8299;
          margin-bottom: 4px;
        }
        .admin-growth-value {
          font-size: 1rem;
          font-weight: 600;
          color: #1BC5BD;
        }
        .admin-search-wrapper {
          position: relative;
          margin-bottom: 20px;
        }
        .admin-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #B5B5C3;
        }
        .admin-search-input {
          width: 100%;
          padding: 12px 12px 12px 44px;
          border: 1px solid #E4E6EF;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
        }
        .admin-search-input:focus {
          outline: none;
          border-color: #6C5CE7;
        }
        .admin-table-wrapper {
          overflow-x: auto;
        }
        .admin-user-cell {
          display: flex;
          flex-direction: column;
        }
        .admin-user-cell .admin-user-name {
          font-weight: 500;
          color: #3F4254;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .admin-user-cell .admin-user-email {
          font-size: 0.85rem;
          color: #7E8299;
        }
        .admin-role-tag {
          font-size: 0.65rem;
          padding: 2px 6px;
          background-color: #6C5CE7;
          color: white;
          border-radius: 3px;
          font-weight: 600;
        }
        .admin-plan-badge {
          padding: 4px 10px;
          background-color: #F3F6F9;
          color: #3F4254;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .admin-action-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .admin-plan-select {
          padding: 6px 10px;
          border: 1px solid #E4E6EF;
          border-radius: 4px;
          font-size: 0.85rem;
          background-color: white;
        }
        .admin-btn {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: opacity 0.2s;
        }
        .admin-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .admin-btn-danger {
          background-color: #FFF5F8;
          color: #F64E60;
        }
        .admin-btn-danger:hover:not(:disabled) {
          background-color: #F64E60;
          color: white;
        }
        .admin-btn-success {
          background-color: #E8FFF3;
          color: #1BC5BD;
        }
        .admin-btn-success:hover:not(:disabled) {
          background-color: #1BC5BD;
          color: white;
        }
        .admin-btn-secondary {
          background-color: #F3F6F9;
          color: #7E8299;
        }
        .admin-btn-secondary:hover:not(:disabled) {
          background-color: #E4E6EF;
        }
        .admin-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #EEF0F8;
        }
        .admin-pagination-info {
          font-size: 0.9rem;
          color: #7E8299;
        }
        .admin-plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
          gap: 20px;
        }
        .admin-plan-card {
          background-color: #F3F6F9;
          border-radius: 8px;
          padding: 24px;
        }
        .admin-plan-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .admin-plan-header h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #181C32;
        }
        .admin-plan-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #6C5CE7;
        }
        .admin-plan-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .admin-plan-features li {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 0;
          border-bottom: 1px solid #E4E6EF;
          font-size: 0.9rem;
        }
        .admin-plan-features li:last-child {
          border-bottom: none;
        }
        .admin-plan-features li.enabled {
          color: #3F4254;
        }
        .admin-plan-features li.disabled {
          color: #B5B5C3;
          text-decoration: line-through;
        }
        .admin-feature-icon {
          width: 20px;
          text-align: center;
          font-weight: bold;
        }
        .admin-plan-features li.enabled .admin-feature-icon {
          color: #1BC5BD;
        }
        .admin-plan-features li.disabled .admin-feature-icon {
          color: #F64E60;
        }
        .admin-feature-limit {
          font-size: 0.8rem;
          color: #7E8299;
          margin-left: 4px;
        }
        .admin-page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 12px;
        }
        .admin-section-nav {
          display: flex;
          gap: 6px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .admin-section-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          border: 1.5px solid #E4E6EF;
          border-radius: 999px;
          background: #FFFFFF;
          color: #7E8299;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .admin-section-btn:hover {
          border-color: #6C5CE7;
          color: #6C5CE7;
        }
        .admin-section-btn--active {
          background: linear-gradient(135deg, #6C5CE7, #A29BFE);
          border-color: transparent;
          color: #FFFFFF;
          box-shadow: 0 3px 10px rgba(108, 92, 231, 0.25);
        }
        @media (max-width: 768px) {
          .admin-action-buttons {
            flex-direction: column;
            gap: 6px;
          }
          .admin-growth-chart {
            gap: 10px;
          }
          .admin-growth-bar {
            min-width: 48px;
          }
          .admin-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .admin-pagination {
            flex-wrap: wrap;
            gap: 8px;
          }
        }
      `}</style>
      <TwoFactorModal
        isOpen={show2fa}
        onClose={handleClose}
        onVerify={handleVerify}
        error={error2fa}
      />
    </AdminLayout>
  );
};

export default AdminDashboard;
